import { Flex, Text } from '@chakra-ui/react';
import {
  ArrowDownLeftFromSquare,
  ArrowUpRightFromSquare,
  Clock4,
  MapPin,
  UsersRound,
} from 'lucide-react';
import {
  FiCheckCircle,
  FiDownload,
  FiPhoneIncoming,
  FiPhoneOutgoing,
  FiRotateCw,
  FiUpload,
  FiXCircle,
} from 'react-icons/fi/index.js';

type Option = {
  label: string;
  value: string;
};

export const ADMIN_NOTE_CATEGORIES: Record<string, Option> = {
  GENERAL_SUPPORT: {
    label: 'General support',
    value: 'GENERAL_SUPPORT',
  },
  TECHNICAL_SUPPORT: {
    label: 'Technical support',
    value: 'TECHNICAL_SUPPORT',
  },
  BUSINESS_SUPPORT: {
    label: 'Business support',
    value: 'BUSINESS_SUPPORT',
  },
  GENERAL: {
    label: 'General',
    value: 'GENERAL',
  },
  WELCOME: {
    label: 'Welcome',
    value: 'WELCOME',
  },
  ENGAGEMENT: {
    label: 'Engagement',
    value: 'ENGAGEMENT',
  },
  FINANCE: {
    label: 'Finance',
    value: 'FINANCE',
  },
  COACHING: {
    label: 'Coaching',
    value: 'COACHING',
  },
  BOOKING: {
    label: 'Booking',
    value: 'BOOKING',
  },
  NOTES: {
    label: 'Notes',
    value: 'NOTES',
  },
  CREDIT_TRANSFER: {
    label: 'Credit transfer (CT)',
    value: 'CREDIT_TRANSFER',
  },
  RECOGNITION_OF_PRIOR_LEARNING: {
    label: 'Recognition of Prior Learning (RPL)',
    value: 'RECOGNITION_OF_PRIOR_LEARNING',
  },
  NOT_REQUIRED: {
    label: 'Not Required',
    value: 'NOT_REQUIRED',
  },
  EXITED_PROVIDER: {
    label: 'Exited provider',
    value: 'EXITED_PROVIDER',
  },
  FOUND_EMPLOYMENT: {
    label: 'Found employment',
    value: 'FOUND_EMPLOYMENT',
  },
  SUPERSEDED: {
    label: 'Superseded',
    value: 'SUPERSEDED',
  },
  INACTIVITY: {
    label: 'Inactivity',
    value: 'INACTIVITY',
  },
  CHANGE_OF_MIND: {
    label: 'Change of mind',
    value: 'CHANGE_OF_MIND',
  },
  EC_REQUEST: {
    label: 'EC request',
    value: 'EC_REQUEST',
  },
  NOT_STARTED: {
    label: 'Not started',
    value: 'NOT_STARTED',
  },
  OTHER: {
    label: 'Other',
    value: 'OTHER',
  },
};

export const ADMIN_NOTE_OUTCOMES: Record<string, Option> = {
  RESOLVED: {
    label: 'Resolved',
    value: 'RESOLVED',
  },
  UNRESOLVED: {
    label: 'Unresolved',
    value: 'UNRESOLVED',
  },
  REBOOKED: {
    label: 'Rebooked',
    value: 'REBOOKED',
  },
  SUCCESSFUL: {
    label: 'Successful',
    value: 'SUCCESSFUL',
  },
  UNSUCCESSFUL: {
    label: 'Unsuccessful',
    value: 'UNSUCCESSFUL',
  },
  BOOKING: {
    label: 'Booking',
    value: 'BOOKING',
  },
};

export const ADMIN_NOTE_OPTIONS: Record<
  string,
  Option & {
    category?: Record<string, Option | undefined> | undefined;
    outcomes?: Record<string, Option | undefined> | undefined;
  }
> = {
  INBOUND_CALL: {
    value: 'INBOUND_CALL',
    label: 'Inbound call',
    category: {
      GENERAL_SUPPORT: ADMIN_NOTE_CATEGORIES.GENERAL_SUPPORT,
      TECHNICAL_SUPPORT: ADMIN_NOTE_CATEGORIES.TECHNICAL_SUPPORT,
      BUSINESS_SUPPORT: ADMIN_NOTE_CATEGORIES.BUSINESS_SUPPORT,
    },
    outcomes: {
      RESOLVED: ADMIN_NOTE_OUTCOMES.RESOLVED,
      UNRESOLVED: ADMIN_NOTE_OUTCOMES.UNRESOLVED,
      REBOOKED: ADMIN_NOTE_OUTCOMES.REBOOKED,
    },
  },
  OUTBOUND_CALL: {
    value: 'OUTBOUND_CALL',
    label: 'Outbound call',
    category: {
      GENERAL: ADMIN_NOTE_CATEGORIES.GENERAL,
      WELCOME: ADMIN_NOTE_CATEGORIES.WELCOME,
      ENGAGEMENT: ADMIN_NOTE_CATEGORIES.ENGAGEMENT,
      FINANCE: ADMIN_NOTE_CATEGORIES.FINANCE,
      COACHING: ADMIN_NOTE_CATEGORIES.COACHING,
    },
    outcomes: {
      SUCCESSFUL: ADMIN_NOTE_OUTCOMES.SUCCESSFUL,
      UNSUCCESSFUL: ADMIN_NOTE_OUTCOMES.UNSUCCESSFUL,
      BOOKING: ADMIN_NOTE_OUTCOMES.BOOKING,
    },
  },
  MEETING: {
    value: 'MEETING',
    label: 'Meeting',
    category: {
      BOOKING: ADMIN_NOTE_CATEGORIES.BOOKING,
      NOTES: ADMIN_NOTE_CATEGORIES.NOTES,
    },
  },
  ONSITE_SUPPORT: {
    value: 'ONSITE_SUPPORT',
    label: 'Onsite support',
    category: {
      COACHING: ADMIN_NOTE_CATEGORIES.COACHING,
    },
    outcomes: {
      SUCCESSFUL: ADMIN_NOTE_OUTCOMES.SUCCESSFUL,
      UNSUCCESSFUL: ADMIN_NOTE_OUTCOMES.UNSUCCESSFUL,
      BOOKING: ADMIN_NOTE_OUTCOMES.BOOKING,
    },
  },
  INBOUND_EMAIL: {
    value: 'INBOUND_EMAIL',
    label: 'Inbound email',
  },
  OUTBOUND_EMAIL: {
    value: 'OUTBOUND_EMAIL',
    label: 'Outbound email',
  },
  INBOUND_SMS: {
    value: 'INBOUND_SMS',
    label: 'Inbound SMS',
  },
  OUTBOUND_SMS: {
    value: 'OUTBOUND_SMS',
    label: 'Outbound SMS',
  },
  UNIT_NOT_REQUIRED: {
    value: 'UNIT_NOT_REQUIRED',
    label: 'Unit not required',
    category: {
      CREDIT_TRANSFER: ADMIN_NOTE_CATEGORIES.CREDIT_TRANSFER,
      RECOGNITION_OF_PRIOR_LEARNING: ADMIN_NOTE_CATEGORIES.RECOGNITION_OF_PRIOR_LEARNING,
      NOT_REQUIRED: ADMIN_NOTE_CATEGORIES.NOT_REQUIRED,
    },
  },
  WITHDRAW_ENROLMENT: {
    value: 'WITHDRAW_ENROLMENT',
    label: 'Withdraw enrolment',
    category: {
      EXITED_PROVIDER: ADMIN_NOTE_CATEGORIES.EXITED_PROVIDER,
      FOUND_EMPLOYMENT: ADMIN_NOTE_CATEGORIES.FOUND_EMPLOYMENT,
      SUPERSEDED: ADMIN_NOTE_CATEGORIES.SUPERSEDED,
      INACTIVITY: ADMIN_NOTE_CATEGORIES.INACTIVITY,
      CHANGE_OF_MIND: ADMIN_NOTE_CATEGORIES.CHANGE_OF_MIND,
      EC_REQUEST: ADMIN_NOTE_CATEGORIES.EC_REQUEST,
      NOT_STARTED: ADMIN_NOTE_CATEGORIES.NOT_STARTED,
      OTHER: ADMIN_NOTE_CATEGORIES.OTHER,
    },
  },
  RESTORE_ENROLMENT: {
    value: 'RESTORE_ENROLMENT',
    label: 'Restore enrolment',
  },
};

export function getNoteOptionIcon(icon: string) {
  switch (icon) {
    case 'INBOUND_CALL':
      return FiPhoneIncoming;
    case 'OUTBOUND_CALL':
      return FiPhoneOutgoing;
    case 'INBOUND_SMS':
      return FiDownload;
    case 'OUTBOUND_SMS':
      return FiUpload;
    case 'INBOUND_EMAIL':
      return ArrowDownLeftFromSquare;
    case 'OUTBOUND_EMAIL':
      return ArrowUpRightFromSquare;
    case 'MEETING':
      return UsersRound;
    case 'ONSITE_SUPPORT':
      return MapPin;
    case 'SUCCESSFUL':
      return FiCheckCircle;
    case 'UNSUCCESSFUL':
      return FiXCircle;
    case 'BOOKING':
      return Clock4;
    case 'RESOLVED':
      return FiCheckCircle;
    case 'UNRESOLVED':
      return FiXCircle;
    case 'REBOOKED':
      return FiRotateCw;
    default:
      return undefined;
  }
}

export function getAdminNoteTypeOptions() {
  return Object.values(ADMIN_NOTE_OPTIONS).map((value) => {
    const Icon = getNoteOptionIcon(value.value);
    return {
      label: (
        <Flex alignItems="center" gap={2}>
          {Icon ? <Icon size="16px" fontWeight="semibold" /> : null}
          <Text>{value.label}</Text>
        </Flex>
      ),
      value: value.value,
    };
  });
}

export function getAdminNoteCategoryOptions(note_type: string) {
  const note = ADMIN_NOTE_OPTIONS[note_type];
  if (!note?.category) {
    return [];
  }

  return Object.values(note.category).map((value: Option | undefined) => ({
    label: value?.label ?? '',
    value: value?.value ?? '',
  }));
}

export function getAdminNoteOutcomeOptions(note_type: string) {
  const note = ADMIN_NOTE_OPTIONS[note_type];
  if (!note?.outcomes) {
    return [];
  }

  return Object.values(note.outcomes).map((value: Option | undefined) => {
    const Icon = getNoteOptionIcon(value?.value ?? '');
    return {
      label: (
        <Flex alignItems="center" gap={2}>
          {Icon ? <Icon size="16px" fontWeight="semibold" /> : null}
          <Text>{value?.label}</Text>
        </Flex>
      ),
      value: value?.value ?? '',
    };
  });
}
